// frontend/src/components/ui/OfflineGuard.tsx
import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, Clock, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getRecentCalculations, type OfflineCalculation } from '../../utils/offlineStorage';

/**
 * OfflineGuard — two responsibilities:
 * 1. Banner: Shows "Offline" / "Back Online" status toast at the top.
 * 2. Offline Panel: When offline, shows last 5 calculations from IndexedDB
 *    so surveyors can review previous results without internet.
 */
export const OfflineGuard: React.FC = () => {
    const [isOffline, setIsOffline] = useState<boolean>(() =>
        typeof navigator !== 'undefined' ? !navigator.onLine : false
    );
    const [showRestored, setShowRestored] = useState<boolean>(false);
    const [recentCalcs, setRecentCalcs] = useState<OfflineCalculation[]>([]);
    const [showPanel, setShowPanel] = useState(false);
    const { lang } = useLanguage();

    useEffect(() => {
        const handleOffline = async () => {
            setIsOffline(true);
            setShowRestored(false);
            // Load cached calculations when going offline
            const calcs = await getRecentCalculations();
            setRecentCalcs(calcs);
            if (calcs.length > 0) setShowPanel(true);
        };

        const handleOnline = () => {
            setIsOffline(false);
            setShowPanel(false);
            setShowRestored(true);
            const timer = setTimeout(() => setShowRestored(false), 4000);
            return () => clearTimeout(timer);
        };

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);

        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, []);

    // ── Status Banner (top of screen) ────────────────────────────────────────
    const renderBanner = () => {
        if (!isOffline && !showRestored) return null;

        return (
            <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[200] animate-in fade-in slide-in-from-top-4 duration-300">
                {isOffline ? (
                    <div className="flex items-center gap-2.5 px-4 py-2 bg-amber-600/90 text-white text-xs font-bold rounded-full shadow-xl backdrop-blur-md border border-amber-400/30">
                        <WifiOff className="w-4 h-4 animate-pulse shrink-0" />
                        <span>
                            {lang === 'tr'
                                ? 'Çevrimdışı Mod — Uygulama önbellekten çalışıyor'
                                : 'Offline Mode — Running from local cache'}
                        </span>
                        {recentCalcs.length > 0 && (
                            <button
                                onClick={() => setShowPanel((p) => !p)}
                                className="ml-1 flex items-center gap-1 text-amber-100 hover:text-white underline underline-offset-2 font-normal"
                            >
                                <Clock className="w-3 h-3" />
                                {lang === 'tr' ? 'Son Hesaplar' : 'Recent'}
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center gap-2.5 px-4 py-2 bg-emerald-600/90 text-white text-xs font-bold rounded-full shadow-xl backdrop-blur-md border border-emerald-400/30">
                        <Wifi className="w-4 h-4 shrink-0" />
                        <span>
                            {lang === 'tr'
                                ? 'İnternet Bağlantısı Yeniden Sağlandı'
                                : 'Connection Restored'}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    // ── Offline Recent Calculations Panel ────────────────────────────────────
    const renderPanel = () => {
        if (!isOffline || !showPanel || recentCalcs.length === 0) return null;

        return (
            <div className="fixed bottom-0 left-0 right-0 z-[190] animate-in fade-in slide-in-from-bottom-4 duration-300 md:bottom-6 md:left-6 md:right-auto md:max-w-sm">
                <div className="bg-slate-900/95 border border-slate-700/60 backdrop-blur-md shadow-2xl rounded-t-2xl md:rounded-2xl">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/40">
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-amber-400" />
                            <span className="text-sm font-semibold text-white">
                                {lang === 'tr' ? 'Son Hesaplamalarınız' : 'Your Recent Calculations'}
                            </span>
                        </div>
                        <span className="text-xs text-slate-500">
                            {lang === 'tr' ? 'Salt Okunur' : 'Read-Only'}
                        </span>
                    </div>

                    {/* Calculation list */}
                    <ul className="divide-y divide-slate-800/60">
                        {recentCalcs.map((calc) => {
                            const date = new Date(calc.timestamp).toLocaleDateString(
                                lang === 'tr' ? 'tr-TR' : 'en-GB',
                                { day: 'numeric', month: 'short' }
                            );
                            return (
                                <li
                                    key={calc.id}
                                    className="flex items-center gap-3 px-4 py-3 group"
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-slate-200 truncate">{calc.summary}</p>
                                        <p className="text-xs text-slate-500 mt-0.5">{date}</p>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-slate-600 shrink-0" />
                                </li>
                            );
                        })}
                    </ul>

                    {/* Footer */}
                    <div className="px-4 py-3 border-t border-slate-800/60">
                        <p className="text-xs text-slate-500 text-center">
                            {lang === 'tr'
                                ? '⚡ Bağlantı sağlandığında yeni hesap yapabilirsiniz'
                                : '⚡ New calculations available when connection is restored'}
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            {renderBanner()}
            {renderPanel()}
        </>
    );
};

export default OfflineGuard;
